import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import {
  DeleteGroupAction,
  EditGroupAction,
  PopulateEditGroupAction,
  SetBackgroundImgEditGroupAction,
  SetGroupDescriptionEditGroupAction,
  SetGroupLocationEditGroupAction,
  SetWebsiteUrlEditGroupAction,
  SetLogoUrlEditGroupAction,
  SetPrivacyEditGroupAction,
  SetSportEditGroupAction,
  SetTitleEditGroupAction,
  SetWhoCanPublishEditGroupAction,
  SetActivitySharingEditGroupAction,
  SetHandiFriendlyEditGroupAction,
} from './edit-group.actions';
import { EMPTY, catchError, map, tap } from 'rxjs';
import { aFailedCallIsReportedAndStops } from '../../../utils/a-failed-call-is-reported-and-stops';
import { GroupsService, GeoLocationEntity } from '@smatchy/sdk';
import { Injectable, inject } from '@angular/core';

import { GetGroupByIdAction } from './group-content.actions';
import { GetMyGroupsAction } from './my-groups.actions';
import { LoadingController } from '@ionic/angular';
import { NavController } from '@ionic/angular';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

export type EditGroupStateModel = {
  _id: string;
  name: string;
  backgroundUrl: string;
  /** The group's own square mark. Empty when none was uploaded. */
  logoUrl: string;
  description: string;
  websiteUrl: string;
  location: GeoLocationEntity | null;
  sportId: string;
  /** SMA-99: every sport of the group. `sportId` above is the first of them. */
  sportIds: string[];
  rightCreateActivity: 'allMembers' | 'onlyAdmin';
  privacy: 'public' | 'private' | 'hidden';
  activitySharing: boolean;
  handiFriendly: boolean;
};

const defaults: EditGroupStateModel = {
  _id: '',
  name: '',
  backgroundUrl: '',
  logoUrl: '',
  description: '',
  websiteUrl: '',
  sportId: '',
  sportIds: [],
  location: null,
  rightCreateActivity: 'allMembers',
  privacy: 'public',
  activitySharing: false,
  handiFriendly: false,
};

@State<EditGroupStateModel>({
  name: 'editGroup',
  defaults,
})
@Injectable()
export class EditGroupState {
  groupService = inject(GroupsService);
  router = inject(Router);
  toastController = inject(ToastController);
  translate = inject(TranslateService);
  loadingController = inject(LoadingController);
  store = inject(Store);
  navCtrl = inject(NavController);

  @Selector()
  static canSave(state: EditGroupStateModel): boolean {
    return EditGroupState.isWebsiteUrlValidValue(state.websiteUrl);
  }

  @Action(EditGroupAction)
  async editGroupAction({ getState }: StateContext<EditGroupStateModel>) {
    // Sentry CAPACITOR-7: "overlay does not exist", on /home/group/:id/.
    //
    // Two causes, both here. `duration: 10000` made the spinner dismiss
    // itself after ten seconds, so an edit that took longer left the later
    // dismiss() with nothing to dismiss, and Ionic throws for that. And
    // present() was not awaited, so a fast failure could dismiss before the
    // overlay had finished appearing, which throws in the same way.
    //
    // The duration is gone rather than lengthened. A spinner that vanishes on
    // its own while the request is still running is a bug of its own: the
    // person sees the form again, concludes nothing happened, and edits or
    // submits a second time. Every path below dismisses explicitly.
    const loading = await this.loadingController.create({
      message: this.translate.instant('GROUPS.EDIT.IS_LOADING'),
    });

    await loading.present();

    const state = getState();
    if (!this.isWebsiteUrlValid(state.websiteUrl)) {
      loading.dismiss();
      const toast = await this.toastController.create({
        message: this.translate.instant('GROUPS.WEBSITE.INVALID_URL'),
        duration: 3000,
        icon: 'warning',
        position: 'top',
        color: 'danger',
      });
      await toast.present();
      return;
    }

    const websiteUrl = state.websiteUrl.trim();

    return this.groupService
      .editGroup({
        id: state._id,
        editGroupDto: {
          name: state.name,
          description: state.description,
          backgroundUrl: state.backgroundUrl,
          // Sent on every save, empty included: an empty string is how the API
          // is told to remove the logo, so leaving it out would make « remove »
          // impossible to express.
          logoUrl: state.logoUrl,
          sportId: state.sportId,
          sportIds: state.sportIds,
          // `?? undefined`: the state clears a location with null, and the DTO
          // leaves the field out rather than sending one.
          location: state.location ?? undefined,
          privacy: state.privacy,
          rightCreateActivity: state.rightCreateActivity,
          websiteUrl,
          allowActivitySharing: state.activitySharing,
          handiFriendly: state.handiFriendly,
        },
      })
      .pipe(
        map(() => {
          this.store.dispatch(new GetGroupByIdAction(state._id));
          this.store.dispatch(new GetMyGroupsAction());
          this.navCtrl.back();
          loading.dismiss();
        }),
        catchError(async (err) => {
          loading.dismiss();
          console.error('Error updating group:', err);
          const toast = await this.toastController.create({
            message: err?.error?.message || err?.message || 'Error updating group',
            duration: 3000,
            color: 'danger',
          });
          await toast.present();
          return EMPTY;
        }),
      );
  }

  @Action(PopulateEditGroupAction)
  async populateEditGroupAction({ setState }: StateContext<EditGroupStateModel>, { payload }: PopulateEditGroupAction) {
    // A group's sport arrives either as its id or as the populated document,
    // depending on the route that answered.
    const sport = payload.group.sport as string | { _id?: string; id?: string } | undefined;
    const sportId = typeof sport === 'string' ? sport : sport?._id || sport?.id || '';

    // SMA-99. A group edited before the migration reached it answers with one
    // sport and no list, and it must still be editable: the list then holds
    // that one sport, so saving the group back does not silently empty it.
    const sports = (payload.group.sports ?? []).map((one) => one?._id).filter((id): id is string => !!id);
    const sportIds = sports.length > 0 ? sports : [sportId].filter(Boolean);

    setState({
      _id: payload.group._id,
      name: payload.group.name,
      backgroundUrl: payload.group.backgroundUrl,
      logoUrl: payload.group.logoUrl ?? '',
      description: payload.group.description,
      websiteUrl: payload.group.websiteUrl ?? '',
      sportId: sportId,
      sportIds,
      // `?? null` because the content state clears its location with null while
      // the field is also optional there, and this model says null for absent.
      location: payload.group.location ?? null,
      rightCreateActivity: payload.group.rightCreateActivity,
      privacy: payload.group.privacy,
      activitySharing: payload.group.allowActivitySharing ?? false,
      handiFriendly: payload.group.handiFriendly ?? false,
    });
  }

  @Action(DeleteGroupAction)
  deleteGroupAction({ getState, setState }: StateContext<EditGroupStateModel>) {
    return this.groupService.removeGroup({ id: getState()._id }).pipe(
      tap(() => {
        this.router.navigate(['/home']);
      }),
      catchError((error) => aFailedCallIsReportedAndStops(error)),
    );
  }

  @Action(SetTitleEditGroupAction)
  async setTitleEditGroupAction(
    { patchState }: StateContext<EditGroupStateModel>,
    { payload }: SetTitleEditGroupAction,
  ) {
    patchState({
      name: payload,
    });
  }

  @Action(SetGroupDescriptionEditGroupAction)
  async SetGroupDescriptionEditGroupAction(
    { patchState }: StateContext<EditGroupStateModel>,
    { payload }: SetGroupDescriptionEditGroupAction,
  ) {
    patchState({
      description: payload,
    });
  }

  @Action(SetWebsiteUrlEditGroupAction)
  async setWebsiteUrlEditGroupAction(
    { patchState }: StateContext<EditGroupStateModel>,
    { payload }: SetWebsiteUrlEditGroupAction,
  ) {
    patchState({
      websiteUrl: payload,
    });
  }

  @Action(SetGroupLocationEditGroupAction)
  async SetGroupLocationEditGroupAction(
    { patchState }: StateContext<EditGroupStateModel>,
    { payload }: SetGroupLocationEditGroupAction,
  ) {
    patchState({
      location: payload,
    });
  }

  @Action(SetLogoUrlEditGroupAction)
  async setLogoUrlEditGroupAction(
    { patchState }: StateContext<EditGroupStateModel>,
    { payload }: SetLogoUrlEditGroupAction,
  ) {
    patchState({
      logoUrl: payload,
    });
  }

  @Action(SetBackgroundImgEditGroupAction)
  async setBackgroundImgEditGroupAction(
    { patchState }: StateContext<EditGroupStateModel>,
    { payload }: SetBackgroundImgEditGroupAction,
  ) {
    patchState({
      backgroundUrl: payload,
    });
  }

  @Action(SetSportEditGroupAction)
  async setSportEditGroupAction(
    { getState, patchState }: StateContext<EditGroupStateModel>,
    { payload }: SetSportEditGroupAction,
  ) {
    patchState({
      sportId: payload.sportId,
      // The first of the chosen sports is the single field, because it is the
      // one that gives the group its cover picture.
      sportIds: payload.sportIds ?? [payload.sportId].filter(Boolean),
    });
  }

  @Action(SetWhoCanPublishEditGroupAction)
  async SetWhoCanPublishEditGroupAction(
    { getState, patchState }: StateContext<EditGroupStateModel>,
    { payload }: SetWhoCanPublishEditGroupAction,
  ) {
    patchState({
      rightCreateActivity: payload.whoCanPublish,
    });
  }

  @Action(SetPrivacyEditGroupAction)
  async SetPrivacyEditGroupAction(
    { getState, patchState }: StateContext<EditGroupStateModel>,
    { payload }: SetPrivacyEditGroupAction,
  ) {
    patchState({
      privacy: payload.privacy,
    });
  }

  private isWebsiteUrlValid(value: string): boolean {
    return EditGroupState.isWebsiteUrlValidValue(value);
  }

  private static isWebsiteUrlValidValue(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed) return true;

    if (!/^https?:\/\//i.test(trimmed) || /\s/.test(trimmed)) {
      return false;
    }

    try {
      const url = new URL(trimmed);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }

  @Action(SetActivitySharingEditGroupAction)
  async setActivitySharingEditGroupAction(
    { patchState }: StateContext<EditGroupStateModel>,
    { payload }: SetActivitySharingEditGroupAction,
  ) {
    patchState({
      activitySharing: payload.activitySharing,
    });
  }

  @Action(SetHandiFriendlyEditGroupAction)
  async setHandiFriendlyEditGroupAction(
    { patchState }: StateContext<EditGroupStateModel>,
    { payload }: SetHandiFriendlyEditGroupAction,
  ) {
    patchState({
      handiFriendly: payload.handiFriendly,
    });
  }
}
